/**
 * Periods are derived from the calendar, never stored as a `next_run`.
 *
 * This is Cleanify's weekly-window lesson applied to allowance. A stored cursor
 * means a missed job skips a week's pay, a retried job pays twice, and a job
 * that runs late pays under whatever rule happens to be current. Deriving the
 * period index from a fixed epoch means every device — and every replay of the
 * same history — computes the same set of periods, so "run the allowance job"
 * becomes "insert the periods that aren't there yet" and is safe to call on
 * every app open.
 *
 * Everything works in calendar dates (`YYYY-MM-DD`), not timestamps. A week
 * boundary that depends on the hour is a week boundary that moves when the
 * family flies somewhere or the clocks change; a family that agrees on what day
 * it is agrees on which week it is.
 */

/** 1970-01-04 was a Sunday — the anchor every weekly period counts from. */
const EPOCH_SUNDAY_DAY_NUMBER = 3;

export const CADENCES = Object.freeze({
  weekly: { days: 7, perYear: 52 },
  biweekly: { days: 14, perYear: 26 },
  monthly: { days: null, perYear: 12 },
});

export const toDateOnly = (date) =>
  date instanceof Date ? date.toISOString().slice(0, 10) : String(date).slice(0, 10);

export function parseDateOnly(date) {
  const [year, month, day] = toDateOnly(date).split('-').map(Number);
  return { year, month, day };
}

/** Days since 1970-01-01, from the date parts alone. */
export function dayNumber(date) {
  const { year, month, day } = parseDateOnly(date);
  return Math.floor(Date.UTC(year, month - 1, day) / 86400000);
}

export function dateFromDayNumber(days) {
  return new Date(days * 86400000).toISOString().slice(0, 10);
}

/**
 * Which period a date falls in. Stable forever: index 2900 is the same week in
 * 2025 as it is when recomputed in 2031.
 */
export function periodIndexAt(date, cadence = 'weekly') {
  if (cadence === 'monthly') {
    const { year, month } = parseDateOnly(date);
    return (year - 1970) * 12 + (month - 1);
  }
  const { days } = CADENCES[cadence] ?? CADENCES.weekly;
  return Math.floor((dayNumber(date) - EPOCH_SUNDAY_DAY_NUMBER) / days);
}

export function periodStart(index, cadence = 'weekly') {
  if (cadence === 'monthly') {
    const year = 1970 + Math.floor(index / 12);
    const month = (index % 12) + 1;
    return `${year}-${String(month).padStart(2, '0')}-01`;
  }
  const { days } = CADENCES[cadence] ?? CADENCES.weekly;
  return dateFromDayNumber(index * days + EPOCH_SUNDAY_DAY_NUMBER);
}

/**
 * Every period that has *started* between two dates, inclusive.
 *
 * A period is payable once it begins, so a family that opens the app after six
 * weeks away gets six entries, in order, each priced by the rules that applied
 * when it began — not six copies of this week's number.
 */
export function periodsBetween(fromDate, toDate, cadence = 'weekly') {
  const first = periodIndexAt(fromDate, cadence);
  const last = periodIndexAt(toDate, cadence);
  const out = [];
  for (let i = first; i <= last; i += 1) out.push(i);
  return out;
}
